
import React, { useState } from 'react';
import { format } from 'date-fns';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useNavigate } from 'react-router-dom';
import { View, CheckCircle2, Calendar as CalendarIcon, DollarSign, Download, X } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { usePaymentConfirmedProcurements } from '@/hooks/use-payment-confirmed-procurements';
import { CustomPagination } from '@/components/ui/custom-pagination';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import * as XLSX from 'xlsx';

const FinanceConfirmedPaymentsList: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const pageSize = 10;
  
  const { data: result, isLoading } = usePaymentConfirmedProcurements({ 
    page: currentPage, 
    pageSize,
    startDate: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
    endDate: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
  });
  const navigate = useNavigate();

  const handleClearDateRange = () => {
    setStartDate(undefined);
    setEndDate(undefined);
    setCurrentPage(1);
  };

  // Type guard to check if result has pagination structure
  const isPaginatedResult = (data: any): data is { data: any[], totalPages: number } => {
    return data && typeof data === 'object' && 'data' in data && 'totalPages' in data;
  };

  const confirmedPayments = isPaginatedResult(result) ? result.data : [];
  const totalPages = isPaginatedResult(result) ? result.totalPages : 0;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const getExportFilename = (extension: string) => {
    const dateRangeStr = startDate && endDate 
      ? `_${format(startDate, 'yyyy-MM-dd')}_to_${format(endDate, 'yyyy-MM-dd')}`
      : startDate 
      ? `_from_${format(startDate, 'yyyy-MM-dd')}`
      : endDate
      ? `_until_${format(endDate, 'yyyy-MM-dd')}`
      : `_${format(new Date(), 'yyyy-MM-dd')}`;
    return `confirmed_payments${dateRangeStr}.${extension}`;
  };

  const exportToCSV = () => {
    const csvData = confirmedPayments.map((procurement) => ({
      'ID': procurement.id,
      'Product Name': procurement.product_name || 'N/A',
      'Source': `${procurement.source_name || 'N/A'} (${procurement.source_type})`,
      'Total Amount (Br)': procurement.total_amount?.toFixed(2) || '0.00',
      'Quantity (kg)': procurement.total_quantity || 0,
      'Damage (kg)': procurement.quality?.damage_kg || 0,
      'Compensation (kg)': procurement.quality?.compensation_kg || 0,
      'Net Payable (kg)': (procurement.total_quantity || 0) - (procurement.quality?.compensation_kg || 0),
      'Payment Date': procurement.finance?.actual_payment_date 
        ? format(new Date(procurement.finance.actual_payment_date), 'PP')
        : 'N/A',
      'Confirmation Date': procurement.finance?.payment_confirmed_at 
        ? format(new Date(procurement.finance.payment_confirmed_at), 'PP')
        : 'N/A',
      'Confirmed By': procurement.finance?.payment_confirmed_by || 'N/A',
      'Advance Payment (Br)': procurement.finance?.advance_payment_amount?.toFixed(2) || 'N/A',
      'Remaining Payment (Br)': procurement.finance?.remaining_payment_amount?.toFixed(2) || 'N/A',
      'Payment Notes': procurement.finance?.payment_confirmation_notes || '',
      'Requestor': procurement.requestor?.full_name || 'N/A',
    }));

    const ws = XLSX.utils.json_to_sheet(csvData);
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', getExportFilename('csv'));
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();

    // Summary sheet
    const summaryData = [
      ['Confirmed Payments Summary'],
      [''],
      ['Total Confirmed Payments', confirmedPayments.length],
      ['Total Amount (Br)', confirmedPayments.reduce((sum, p) => sum + (p.total_amount || 0), 0).toFixed(2)],
      ['Total Quantity (kg)', confirmedPayments.reduce((sum, p) => sum + (p.total_quantity || 0), 0).toFixed(2)],
      ['Export Date', format(new Date(), 'PPpp')],
      ['Page', currentPage],
      ['Date Range', startDate && endDate 
        ? `${format(startDate, 'PP')} - ${format(endDate, 'PP')}`
        : startDate 
        ? `From ${format(startDate, 'PP')}`
        : endDate
        ? `Until ${format(endDate, 'PP')}`
        : 'All dates'],
    ];
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');

    // Data sheet
    const detailData = confirmedPayments.map((procurement) => ({
      'ID': procurement.id,
      'Product Name': procurement.product_name || 'N/A',
      'Source': `${procurement.source_name || 'N/A'} (${procurement.source_type})`,
      'Total Amount (Br)': procurement.total_amount?.toFixed(2) || '0.00',
      'Quantity (kg)': procurement.total_quantity || 0,
      'Damage (kg)': procurement.quality?.damage_kg || 0,
      'Compensation (kg)': procurement.quality?.compensation_kg || 0,
      'Net Payable (kg)': (procurement.total_quantity || 0) - (procurement.quality?.compensation_kg || 0),
      'Payment Date': procurement.finance?.actual_payment_date 
        ? format(new Date(procurement.finance.actual_payment_date), 'PP')
        : 'N/A',
      'Confirmation Date': procurement.finance?.payment_confirmed_at 
        ? format(new Date(procurement.finance.payment_confirmed_at), 'PP')
        : 'N/A',
      'Confirmed By': procurement.finance?.payment_confirmed_by || 'N/A',
      'Advance Payment (Br)': procurement.finance?.advance_payment_amount?.toFixed(2) || 'N/A',
      'Remaining Payment (Br)': procurement.finance?.remaining_payment_amount?.toFixed(2) || 'N/A',
      'Payment Notes': procurement.finance?.payment_confirmation_notes || '',
      'Requestor': procurement.requestor?.full_name || 'N/A',
    }));
    const dataSheet = XLSX.utils.json_to_sheet(detailData);
    XLSX.utils.book_append_sheet(wb, dataSheet, 'Confirmed Payments');

    XLSX.writeFile(wb, getExportFilename('xlsx'));
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-32 w-full" />
        ))}
      </div>
    );
  }

  if (confirmedPayments.length === 0) {
    return (
      <div className="text-center py-8">
        <CheckCircle2 className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
        <p className="text-muted-foreground">No confirmed payments found.</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col sm:flex-row gap-4 mb-4">
        <div className="flex flex-wrap gap-2 flex-1">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className={cn(!startDate && "text-muted-foreground")}>
                <CalendarIcon className="mr-2 h-4 w-4" />
                {startDate ? format(startDate, 'PP') : 'Start Date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={startDate}
                onSelect={(date) => {
                  setStartDate(date);
                  setCurrentPage(1);
                }}
                initialFocus
                className="pointer-events-auto"
              />
            </PopoverContent>
          </Popover>

          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className={cn(!endDate && "text-muted-foreground")}>
                <CalendarIcon className="mr-2 h-4 w-4" />
                {endDate ? format(endDate, 'PP') : 'End Date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={endDate}
                onSelect={(date) => {
                  setEndDate(date);
                  setCurrentPage(1);
                }}
                initialFocus
                className="pointer-events-auto"
              />
            </PopoverContent>
          </Popover>

          {(startDate || endDate) && (
            <Button variant="ghost" size="sm" onClick={handleClearDateRange}>
              <X className="h-4 w-4 mr-1" />
              Clear
            </Button>
          )}
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={exportToCSV}>
              Export as CSV
            </DropdownMenuItem>
            <DropdownMenuItem onClick={exportToExcel}>
              Export as Excel
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="space-y-4">
        {confirmedPayments.map((procurement) => (
          <Card key={procurement.id}>
            <CardContent className="p-4">
              <div className="flex justify-between items-start">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium">{procurement.product_name || 'N/A'}</h3>
                    <Badge className="bg-green-100 text-green-800">Payment Confirmed</Badge>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <DollarSign className="h-4 w-4" />
                      <span className="font-medium">Amount:</span> Br{procurement.total_amount?.toFixed(2) || '0.00'}
                    </div>
                    <div>
                      <span className="font-medium">Quantity:</span> {procurement.total_quantity || 0} kg
                    </div>
                    <div className="flex items-center gap-1">
                      <CalendarIcon className="h-4 w-4" />
                      <span className="font-medium">Payment Date:</span> {
                        procurement.finance?.actual_payment_date 
                          ? format(new Date(procurement.finance.actual_payment_date), 'PP')
                          : 'N/A'
                      }
                    </div>
                    <div className="flex items-center gap-1">
                      <CalendarIcon className="h-4 w-4" />
                      <span className="font-medium">Confirmed:</span> {
                        procurement.finance?.payment_confirmed_at 
                          ? format(new Date(procurement.finance.payment_confirmed_at), 'PP')
                          : 'N/A'
                      }
                    </div>
                  </div>

                  {/* Quality Inspection Findings */}
                  {(procurement.quality?.damage_kg > 0 || procurement.quality?.compensation_kg > 0) && (
                    <div className="bg-amber-50 border border-amber-200 rounded-md p-2 text-sm">
                      <div className="grid grid-cols-3 gap-2 text-amber-700">
                        <div>
                          <span className="font-medium">Damage:</span> {procurement.quality?.damage_kg || 0} kg
                        </div>
                        <div>
                          <span className="font-medium">Compensation:</span> {procurement.quality?.compensation_kg || 0} kg
                        </div>
                        <div>
                          <span className="font-medium">Net Payable:</span> {(procurement.total_quantity || 0) - (procurement.quality?.compensation_kg || 0)} kg
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="text-sm">
                    <span className="font-medium">Source:</span> {procurement.source_name || 'N/A'} ({procurement.source_type})
                  </div>
                  
                  {procurement.finance?.payment_confirmation_notes && (
                    <div className="text-sm">
                      <span className="font-medium">Notes:</span> {procurement.finance.payment_confirmation_notes}
                    </div>
                  )}
                </div>
                
                <div className="ml-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/procurements/${procurement.id}`)}
                  >
                    <View className="mr-1 h-4 w-4" />
                    View Details
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {totalPages > 1 && (
        <CustomPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          className="mt-6"
        />
      )}
    </>
  );
};

export default FinanceConfirmedPaymentsList;
